import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Coins, RefreshCw, Scale, Users, Wrench } from "lucide-react";

import api from "../../api";

function amountG(value) {
  return `${Number(value || 0).toFixed(4)} g`;
}

function currentMonthStart() {
  return `${new Date().toISOString().slice(0, 8)}01`;
}

function SilverStockLedger() {
  const today = new Date().toISOString().slice(0, 10);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    current_balance_g: 0,
    total_in_g: 0,
    total_out_g: 0,
    total_wastage_g: 0,
    ledger: [],
    stock_by_customer: [],
    production_usage: [],
  });
  const [filters, setFilters] = useState({
    customer_id: "",
    view: "MONTH",
    date_from: currentMonthStart(),
    date_to: today,
  });

  const loadCustomers = useCallback(async () => {
    const res = await api.get("/silver-stock/meta");
    setCustomers(res.data?.customers || []);
  }, []);

  const loadLedger = useCallback(async (nextFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        customer_id: nextFilters.customer_id || "",
        view: nextFilters.view,
        date_from: nextFilters.date_from || "",
        date_to: nextFilters.date_to || "",
      });
      const res = await api.get(`/silver-stock/ledger?${params.toString()}`);
      setData(res.data || {});
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load silver stock ledger.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        await Promise.all([loadCustomers(), loadLedger({
          customer_id: "",
          view: "MONTH",
          date_from: currentMonthStart(),
          date_to: today,
        })]);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load silver stock setup.");
        setLoading(false);
      }
    };
    run();
  }, [loadCustomers, loadLedger, today]);

  const handleFilterChange = async (key, value) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    await loadLedger(nextFilters);
  };

  const ledgerRows = useMemo(() => data.ledger || [], [data.ledger]);
  const customerRows = useMemo(() => data.stock_by_customer || [], [data.stock_by_customer]);
  const usageRows = useMemo(() => data.production_usage || [], [data.production_usage]);

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard icon={Coins} label="Total Received" value={amountG(data.total_in_g)} tone="emerald" />
        <KpiCard icon={Wrench} label="Total Issued" value={amountG(data.total_out_g)} tone="amber" />
        <KpiCard icon={Scale} label="Total Wastage" value={amountG(data.total_wastage_g)} tone="rose" />
        <KpiCard icon={Users} label="Current Balance" value={amountG(data.current_balance_g)} />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Silver Stock Ledger</h3>
            <p className="text-sm text-slate-500">Client silver lots, production issuance, stage wastage, and running stock balance.</p>
          </div>
          <button type="button" onClick={() => loadLedger(filters)} className="secondary-btn">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>

        <div className="grid gap-3 border-b border-slate-100 p-6 md:grid-cols-2 xl:grid-cols-5">
          <Field label="Customer">
            <select value={filters.customer_id} onChange={(event) => handleFilterChange("customer_id", event.target.value)} className="input">
              <option value="">All Customers</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name} ({customer.code})</option>
              ))}
            </select>
          </Field>
          <Field label="View">
            <select value={filters.view} onChange={(event) => handleFilterChange("view", event.target.value)} className="input">
              <option value="DAY">Day</option>
              <option value="WEEK">Week</option>
              <option value="MONTH">Month</option>
              <option value="ALL">Custom</option>
            </select>
          </Field>
          <Field label="From">
            <input type="date" value={filters.date_from} onChange={(event) => handleFilterChange("date_from", event.target.value)} className="input" />
          </Field>
          <Field label="To">
            <input type="date" value={filters.date_to} onChange={(event) => handleFilterChange("date_to", event.target.value)} className="input" />
          </Field>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Serial No</th>
                <th className="px-4 py-3">Silver Type</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Job Card</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">In(g)</th>
                <th className="px-4 py-3">Out(g)</th>
                <th className="px-4 py-3">Wastage(g)</th>
                <th className="px-4 py-3">Balance(g)</th>
              </tr>
            </thead>
            <tbody>
              {!loading && ledgerRows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">{row.date}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      row.type === "INTAKE" ? "bg-emerald-100 text-emerald-700" :
                      row.type === "PRODUCTION_ISSUE" ? "bg-amber-100 text-amber-700" :
                      row.type === "WASTAGE" ? "bg-rose-100 text-rose-700" :
                      row.type === "PRODUCTION_RETURN" ? "bg-sky-100 text-sky-700" :
                      "bg-slate-100 text-slate-700"
                    }`}>
                      {row.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">{row.serial_no || "-"}</td>
                  <td className="px-4 py-3">{row.silver_type || "-"}</td>
                  <td className="px-4 py-3">{row.customer_name || "-"}</td>
                  <td className="px-4 py-3">{row.job_card_no || "-"}</td>
                  <td className="px-4 py-3">{row.stage_code || "-"}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-700">{Number(row.in_g || 0).toFixed(4)}</td>
                  <td className="px-4 py-3 font-semibold text-amber-700">{Number(row.out_g || 0).toFixed(4)}</td>
                  <td className="px-4 py-3 font-semibold text-rose-700">{Number(row.wastage_g || 0).toFixed(4)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{Number(row.balance_g || 0).toFixed(4)}</td>
                </tr>
              ))}
              {!loading && !ledgerRows.length && (
                <tr>
                  <td colSpan="11" className="px-4 py-10 text-center text-slate-500">No silver stock movements found.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="11" className="px-4 py-10 text-center text-slate-500">Loading ledger...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-2">
        <SummaryTable
          title="Stock by Customer"
          description="Each client contribution to silver stock."
          emptyText="No customer stock data found."
          headers={["Customer", "Received(g)", "Issued(g)", "Wastage(g)", "Balance(g)"]}
          rows={customerRows.map((row) => [
            row.customer_name || "-",
            Number(row.received_g || 0).toFixed(4),
            Number(row.issued_g || 0).toFixed(4),
            Number(row.wastage_g || 0).toFixed(4),
            Number(row.balance_g || 0).toFixed(4),
          ])}
        />
        <SummaryTable
          title="Production Usage"
          description="Silver issued and reconciled per job card."
          emptyText="No production usage found."
          headers={["Job Card", "Issued(g)", "Returned(g)", "Wastage(g)", "Net Used(g)", "Stages"]}
          rows={usageRows.map((row) => [
            row.job_card_no || String(row.job_card_id || "-"),
            Number(row.issued_g || 0).toFixed(4),
            Number(row.returned_g || 0).toFixed(4),
            Number(row.wastage_g || 0).toFixed(4),
            Number(row.net_used_g || 0).toFixed(4),
            (row.stages || []).join(", ") || "-",
          ])}
        />
      </section>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .secondary-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "slate" }) {
  const toneClasses = {
    slate: "border-indigo-100 bg-indigo-50 text-indigo-700",
    emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    rose: "border-rose-100 bg-rose-50 text-rose-700",
  };
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${toneClasses[tone] || toneClasses.slate}`}>
          <Icon size={22} />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
        </div>
      </div>
    </div>
  );
}

function SummaryTable({ title, description, headers, rows, emptyText }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-6 py-5">
        <h3 className="text-xl font-bold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {headers.map((header) => (
                <th key={header} className="px-4 py-3">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className="border-t border-slate-100">
                {row.map((cell, cellIndex) => (
                  <td key={`${index}-${cellIndex}`} className="px-4 py-3">{cell}</td>
                ))}
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={headers.length} className="px-4 py-10 text-center text-slate-500">{emptyText}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SilverStockLedger;
